import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import { Editor } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { Poppins, Spacer, Tooltip } from 'src/common';
import { Variable } from 'src/api/types';
import { useStateSelector } from 'src/redux';
import { formatCurrency } from 'src/utils/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { InfoIcon } from './comps/InfoIcon';
import { ResetBtn } from './comps/ResetBtn';
import {
  DEFAULT_FORMULA_ERROR_MESSAGE,
  INVALID_SYNTAX_FORMULA_ERROR_MESSAGE,
  customSuggestionsFilter,
  evaluate,
  expressionToFormula,
  findVariableByToken,
  formatExpression,
  isWorkspaceOrGlobalVariable,
  validateFormulaSyntax,
} from './shared/formula';
import { Div } from './styled';
import { VariablesContext } from './context';
import { VariableMention } from './extension/VariableMention';
import { SuggestionList, SuggestionListRef } from './comps/SuggestionList';

/**
 * Tiptap-based replacement for `FormulaInput`. Drop-in: identical prop contract
 * (`initaialExpression` misspelling kept on purpose).
 *
 * Phases 2/3/5 implemented: single-line editor, eval pipeline, chrome, variable chips,
 * (de)serialization, and the `#` suggestion dropdown (display + insert). The inline
 * "+ Add Value" editor in dropdown rows is Phase 6 (props accepted, not wired yet).
 */
interface FormulaInputProps {
  variables: Variable[];
  initaialExpression?: string | null;
  onChange: (expression: string) => void;
  onEval: (value: number) => void;
  error?: boolean;
  onErr?: (err: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  helperText?: string | false;
  InputAdornmentStart?: React.ReactNode;
  dataCy?: string;
  onReset?: () => void;
  customResultComponent?: JSX.Element | false;
  hideResult?: boolean;
  onSaveVariableValue?: (config: { variableId: number; value: number; currency: boolean }) => void | Promise<void>;
  onAddWorkspaceVariable?: () => void;
  onAddAssessmentVariable?: () => void;
  // Pulse-highlight the input box (used to draw attention when opened from a
  // "missing variable" prompt).
  highlight?: boolean;
}

// Own the suggestion's plugin key so we can dismiss it programmatically (outside-click).
const formulaSuggestionKey = new PluginKey('formulaSuggestion');

// Serialize the Tiptap doc to a "#<id>" expression string.
const serialize = (editor: Editor): string => {
  let out = '';
  editor.state.doc.descendants((node) => {
    if (node.isText) out += node.text ?? '';
    else if (node.type.name === 'mention') out += `#${node.attrs.id}`;
    return true;
  });
  return out.trim();
};

// Deserialize a "#<id>" expression into Tiptap doc JSON (text runs + mention nodes).
// Mirrors the old createEditorStateFromExpression: formatExpression spacing + token lookup;
// unknown tokens fall back to literal text.
const deserialize = (expression: string | null | undefined, variables: Variable[]) => {
  const formatted = formatExpression(expression);
  if (!formatted) return '';

  const nodes: any[] = [];
  const pushText = (t: string) => {
    if (t) nodes.push({ type: 'text', text: t });
  };

  const regex = /#([A-Za-z0-9_]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(formatted)) !== null) {
    pushText(formatted.slice(last, m.index));
    const variable = findVariableByToken(variables, m[1]);
    if (variable) {
      nodes.push({ type: 'mention', attrs: { id: variable.id, label: variable.alias || variable.name } });
    } else {
      pushText(m[0]);
    }
    last = m.index + m[0].length;
  }
  pushText(formatted.slice(last));

  if (!nodes.length) return '';
  return { type: 'doc', content: [{ type: 'paragraph', content: nodes }] };
};

const FormulaInput: React.FC<FormulaInputProps> = (props) => {
  const {
    initaialExpression = '',
    onChange,
    onEval,
    error,
    onErr,
    helperText,
    InputAdornmentStart,
    dataCy,
    variables,
    onReset,
    customResultComponent,
    hideResult,
    onAddWorkspaceVariable,
    onAddAssessmentVariable,
    highlight,
  } = props;

  const colors = useStateSelector(({ theme }) => theme.colors);
  const [expr, setExpr] = useState<string>(() => formatExpression(initaialExpression));
  const [result, setResult] = useState(0);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const workspaceVariableNames = useMemo(
    () => new Set(variables.filter((v) => isWorkspaceOrGlobalVariable(v)).map((v) => v.name)),
    [variables],
  );

  // Latest values for closures baked into the editor config (created once on mount).
  const liveRef = useRef({
    variables,
    onAddWorkspaceVariable,
    onAddAssessmentVariable,
    onSaveVariableValue: props.onSaveVariableValue,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
  });
  liveRef.current = {
    variables,
    onAddWorkspaceVariable,
    onAddAssessmentVariable,
    onSaveVariableValue: props.onSaveVariableValue,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
  };
  const suggestionOpenRef = useRef(false);

  const editor = useEditor({
    // No markdown auto-formatting in a formula: `*`, `**`, `` ` ``, `1.`, `>` etc. must stay
    // literal (and a buggy input rule on `*`/spaces could throw `Position out of range`).
    enableInputRules: false,
    enablePasteRules: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        // marks have no meaning in a formula
        bold: false,
        italic: false,
        strike: false,
        code: false,
      }),
      VariableMention.configure({
        // Backspace deletes the whole chip and leaves nothing (matches the old component).
        // Without this, Mention re-inserts its trigger char (defaults to "@") in the chip's place.
        deleteTriggerWithBackspace: true,
        suggestion: {
          pluginKey: formulaSuggestionKey,
          char: '#',
          allowedPrefixes: null, // allow `#` right after operators e.g. 5000+#
          items: ({ query }) => customSuggestionsFilter(query, liveRef.current.variables),
          command: ({ editor, range, props: attrs }) => {
            const nodeAfter = editor.view.state.selection.$to.nodeAfter;
            const overrideSpace = nodeAfter?.text?.startsWith(' ');
            if (overrideSpace) range.to += 1;
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                { type: 'mention', attrs },
                { type: 'text', text: ' ' },
              ])
              .run();
            window.getSelection()?.collapseToEnd();

            const variable = liveRef.current.variables.find((v) => v.id === Number(attrs.id));
            mpEvent(MPEvents.VariableAddedToFormula, {
              location: 'Modal:Projected risk:Scenario',
              variableType: variable && isWorkspaceOrGlobalVariable(variable) ? 'Workspace Variable' : 'Assessment Variable',
            });
          },
          render: () => {
            let component: ReactRenderer | null = null;
            let el: HTMLDivElement | null = null;
            let ed: Editor | null = null;
            let editing = false; // true while a row's inline value-edit is in progress

            const place = (clientRect: any) => {
              if (!clientRect || !el) return;
              computePosition({ getBoundingClientRect: clientRect }, el, {
                placement: 'bottom-start',
                middleware: [offset(6), flip(), shift({ padding: 8 })],
              }).then(({ x, y }) => el && Object.assign(el.style, { left: `${x}px`, top: `${y}px` }));
            };
            // Close on click outside both the popup and the editor field. The editor blurring
            // alone doesn't deactivate the suggestion, so dismiss the plugin explicitly.
            const onDocMouseDown = (e: MouseEvent) => {
              const target = e.target as Node;
              if (el?.contains(target)) return;
              const fiRoot = ed?.view.dom.closest('.fi-root');
              if (fiRoot && fiRoot.contains(target)) return;
              editing = false;
              ed?.view.dispatch(ed.view.state.tr.setMeta(formulaSuggestionKey, { exit: true }));
            };
            const cleanup = () => {
              suggestionOpenRef.current = false;
              editing = false;
              document.removeEventListener('mousedown', onDocMouseDown, true);
              el?.remove();
              el = null;
              component?.destroy();
              component = null;
            };
            const extra = () => ({
              onAddWorkspaceVariable: liveRef.current.onAddWorkspaceVariable,
              onAddAssessmentVariable: liveRef.current.onAddAssessmentVariable,
              onSaveVariableValue: liveRef.current.onSaveVariableValue,
              setEditing: (v: boolean) => {
                editing = v;
              },
              closePopup: cleanup,
            });

            return {
              onStart: (sProps: any) => {
                suggestionOpenRef.current = true;
                ed = sProps.editor;
                mpEvent(MPEvents.FormulaVariablesOpen, { location: 'Modal:Projected risk:Scenario' });
                component = new ReactRenderer(SuggestionList, {
                  props: { ...sProps, ...extra() },
                  editor: sProps.editor,
                });
                el = document.createElement('div');
                el.style.position = 'absolute';
                el.style.top = '0';
                el.style.left = '0';
                el.style.zIndex = '9999';
                document.body.appendChild(el);
                el.appendChild(component.element);
                place(sProps.clientRect);
                // capture phase so it fires before React handlers stop propagation
                document.addEventListener('mousedown', onDocMouseDown, true);
              },
              onUpdate: (sProps: any) => {
                component?.updateProps({ ...sProps, ...extra() });
                place(sProps.clientRect);
              },
              onKeyDown: (sProps: any) => (component?.ref as SuggestionListRef | null)?.onKeyDown(sProps) ?? false,
              onExit: () => {
                // The editor blurs when the inline input takes focus → suggestion deactivates
                // and fires onExit. Keep the popup (and its focused input) alive while editing.
                if (editing) return;
                cleanup();
              },
            };
          },
        },
      }),
    ],
    content: deserialize(initaialExpression, variables),
    editorProps: {
      attributes: dataCy ? { 'data-cy': dataCy } : {},
      handleKeyDown: (_view, event) => {
        // single-line: block Enter (newline), but let the dropdown handle Enter to select.
        if (event.key === 'Enter') return suggestionOpenRef.current ? false : true;
        return false;
      },
    },
    onFocus: () => liveRef.current.onFocus?.(),
    onBlur: () => liveRef.current.onBlur?.(),
    onUpdate: ({ editor }) => setExpr(serialize(editor)),
  });

  // Evaluation pipeline — mirrors the old useEffect([variables, editorState]).
  useEffect(() => {
    onChange(expr);
    const formula = expressionToFormula(expr, variables);
    if (formula) {
      if (!validateFormulaSyntax(formula)) {
        setErrMsg(INVALID_SYNTAX_FORMULA_ERROR_MESSAGE);
        onErr?.(INVALID_SYNTAX_FORMULA_ERROR_MESSAGE);
      } else {
        evaluate(formula)
          .then((val) => {
            onEval(val);
            onErr?.('');
            setResult(val);
            setErrMsg(null);
          })
          .catch(() => {
            setErrMsg(DEFAULT_FORMULA_ERROR_MESSAGE);
            onErr?.(DEFAULT_FORMULA_ERROR_MESSAGE);
          });
      }
    } else {
      onEval(0);
      onErr?.('');
      setErrMsg('');
      setResult(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expr, variables]);

  const handleReset = () => {
    if (!editor) return;
    editor.commands.setContent(deserialize(initaialExpression, variables));
    setExpr(serialize(editor));
  };

  const isErrorResult = errMsg === DEFAULT_FORMULA_ERROR_MESSAGE || errMsg === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE;

  return (
    <VariablesContext.Provider value={{ variables, workspaceVariableNames }}>
      <Div $error={error} $highlight={highlight}>
        <div className="fi-root" onClick={() => editor?.chain().focus().run()}>
          {InputAdornmentStart && <div className="fi-root__adorment-start">{InputAdornmentStart}</div>}
          <div className="fi-root__editor">
            <EditorContent editor={editor} />
          </div>
        </div>

        {helperText && (
          <Poppins px={14} className="fi-helper-text">
            {helperText}
          </Poppins>
        )}

        {!hideResult && (
          <>
            <Spacer $px={14} />
            <div className="fi-result">
              <Tooltip
                dataId="fi-next-help"
                place="bottom"
                dataHtml={
                  'Enter a numeric value or a formula.<br/>' +
                  'E.g: 12500 - 5.5 * ( 20000 / 35 ) + 1000<br/>' +
                  'Allowed characters are numbers and + - / * ( ) .<br/>' +
                  'Press # to open variables.'
                }
              >
                <InfoIcon />
              </Tooltip>
              {customResultComponent ? (
                customResultComponent
              ) : (
                <Poppins px={14}>
                  Result:{' '}
                  <span style={{ color: isErrorResult ? colors.error : colors.prussianBlue }}>
                    {errMsg ? errMsg : formatCurrency(result)}
                  </span>
                </Poppins>
              )}
              {onReset && <ResetBtn onClick={handleReset} />}
            </div>
          </>
        )}
      </Div>
    </VariablesContext.Provider>
  );
};

export default FormulaInput;
